import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import * as Survey from '@core/survey/survey'
import * as Record from '@core/record/record'
import * as RecordStep from '@core/record/recordStep'

import * as User from '@core/user/user'
import { Authorizer } from '@openforis/arena-core'
import { useAsyncGetRequest } from '@webapp/components/hooks'

import { useSurveyId, useSurveyInfo } from '@webapp/store/survey'
import { NotificationActions } from '@webapp/store/ui'
import * as API from '@webapp/service/api'

import * as UserState from './state'

export const useUser = () => useSelector(UserState.getUser)
export const useUserIsSystemAdmin = () => User.isSystemAdmin(useUser())

// ====== Auth
// ====== Auth / Surveys
export const useAuthCanCreateSurvey = () => Authorizer.canCreateSurvey(useUser(), useSurveyInfo())
export const useAuthIsMaxSurveysCountReached = () => {
  const user = useUser()
  const maxSurveysCount = Authorizer.getMaxSurveysUserCanCreate(user)
  if (Number.isNaN(maxSurveysCount)) return false
  const surveysCount = (User.getSurveysCountDraft(user) ?? 0) + (User.getSurveysCountPublished(user) ?? 0)
  return surveysCount >= maxSurveysCount
}

export const useOnNewSurveyClick = ({ onAfterClick } = {}) => {
  const dispatch = useDispatch()
  const user = useUser()
  const isMaxSurveysCountReached = useAuthIsMaxSurveysCountReached()

  return useCallback(
    (e) => {
      if (isMaxSurveysCountReached) {
        e.preventDefault()
        const maxSurveysCount = Authorizer.getMaxSurveysUserCanCreate(user)
        dispatch(
          NotificationActions.notifyError({
            key: 'surveyCreate:errorMaxSurveysCountExceeded',
            params: { maxSurveysCount },
          })
        )
      }
      onAfterClick?.()
    },
    [isMaxSurveysCountReached, user, dispatch, onAfterClick]
  )
}

export const useAuthCanEditSurvey = () => Authorizer.canEditSurvey(useUser(), useSurveyInfo())
export const useAuthCanExportSurvey = () => Authorizer.canExportSurvey(useUser(), useSurveyInfo())
export const useAuthCanViewTemplates = () => Authorizer.canViewTemplates(useUser())
export const useAuthCanCreateTemplate = () => Authorizer.canCreateTemplate(useUser())
export const useAuthCanEditTemplates = () => Authorizer.canEditTemplates(useUser())
export const useAuthCanExportSurveysList = () => Authorizer.canExportSurveysList(useUser())
export const useAuthCanEditSurveyOwner = () => Authorizer.canEditSurveyOwner(useUser())

// ====== Auth / Analysis
export const useAuthCanUseAnalysis = () => Authorizer.canAnalyzeRecords(useUser(), useSurveyInfo())

// ====== Auth / Records
export const useAuthCanCreateRecord = () => Authorizer.canCreateRecord(useUser(), useSurveyInfo())
const _canEditRecord = ({ user, surveyInfo, record }) => {
  const canEdit = Authorizer.canEditRecord(user, record)
  return canEdit && (Survey.isPublished(surveyInfo) || Record.isPreview(record))
}
export const useAuthCanEditRecords = (records) => {
  const surveyInfo = useSurveyInfo()
  const user = useUser()
  return records.length > 0 && records.every((record) => _canEditRecord({ user, surveyInfo, record }))
}
export const useAuthCanEditRecord = (record) => useAuthCanEditRecords(record ? [record] : [])
export const useAuthCanDeleteRecords = (records) => useAuthCanEditRecords(records)
export const useAuthCanDeleteRecord = (record) => useAuthCanDeleteRecords([record])
export const useAuthCanCleanseRecords = () => Authorizer.canCleanseRecords(useUser(), useSurveyInfo())
export const useAuthCanPromoteRecord = (record) =>
  useAuthCanEditRecord(record) && RecordStep.getNextStep(Record.getStep(record))
export const useAuthCanDemoteRecord = (record) => {
  // always use the same number of hooks in every call
  const surveyInfo = useSurveyInfo()
  const user = useUser()

  // when record doesn't have a previous step, it cannot be demoted
  if (!RecordStep.getPreviousStep(Record.getStep(record))) return false

  const canDemote = Authorizer.canDemoteRecord(user, record)
  return canDemote && Survey.isPublished(surveyInfo)
}
export const useAuthCanDeleteAllRecords = () => useAuthCanEditSurvey()
export const useAuthCanUpdateRecordsStep = () => Authorizer.canUpdateRecordsStep(useUser(), useSurveyInfo())
export const useAuthCanExportRecordsList = () => Authorizer.canExportRecordsList(useUser(), useSurveyInfo())
export const useAuthCanExportRecords = () => Authorizer.canExportRecords(useUser(), useSurveyInfo())
export const useAuthCanValidateAllRecords = useUserIsSystemAdmin

// ====== Auth / Map
export const useAuthCanUseMap = () => Authorizer.canUseMap(useUser(), useSurveyInfo())

export const useAuthCanUseMessages = useUserIsSystemAdmin

// ====== Auth / Users
export const useAuthCanCreateUsers = () => Authorizer.canCreateUsers(useUser())
export const useAuthCanEditUser = (user) => Authorizer.canEditUser(useUser(), useSurveyInfo(), user)
export const useAuthCanInviteUser = () => Authorizer.canInviteUsers(useUser(), useSurveyInfo())
export const useAuthCanManageUserGroups = () => Authorizer.canManageUserGroups(useUser(), useSurveyInfo())
export const useAuthCanEditQualifierAttributeValue = () =>
  Authorizer.canEditQualifierAttributeValue(useUser(), useSurveyInfo())
export const useAuthCanViewOtherUsersName = () =>
  Authorizer.canViewOtherUsersNameInSameSurvey(useUser(), useSurveyInfo())
export const useAuthCanViewOtherUsersEmail = () =>
  Authorizer.canViewOtherUsersEmail({ user: useUser(), surveyInfo: useSurveyInfo() })
export const useAuthCanViewUsersAccessRequests = () => Authorizer.canViewUsersAccessRequests(useUser())
export const useAuthCanViewAllUsers = () => Authorizer.canViewAllUsers(useUser())

// ====== Profile picture
export const useProfilePicture = ({ userUuid = null, forceUpdateKey = null }) => {
  const { data = null, dispatch: fetchUserProfilePicture } = useAsyncGetRequest(
    `/api/user/${userUuid}/profilePicture`,
    { responseType: 'blob' }
  )

  useEffect(() => {
    if (userUuid) {
      fetchUserProfilePicture()
    }
  }, [userUuid, forceUpdateKey])

  const [profilePicture, setProfilePicture] = useState(null)

  useEffect(() => {
    if (data && data.size > 0) {
      const objectUrl = URL.createObjectURL(data)
      setProfilePicture(objectUrl)
      return () => {
        URL.revokeObjectURL(objectUrl)
      }
    }
    setProfilePicture(null)
    return undefined
  }, [data])

  return profilePicture
}

export const useUserName = ({ userUuid, active = true }) => {
  const [userName, setUserName] = useState(null)

  const canViewUsersName = useAuthCanViewOtherUsersName()
  const surveyId = useSurveyId()

  useEffect(() => {
    if (canViewUsersName && userUuid && active) {
      void API.fetchUserName({ userUuid, surveyId }).then((name) => {
        setUserName(name)
      })
    }
  }, [active, canViewUsersName, surveyId, userUuid])

  return userName
}
